import { C100Row, ObOsState } from '../../_types/carnivore100.type'

export type SortKey =
  | 'rank' | 'ticker' | 'name' | 'sector' | 'market'
  | 'price' | 'dayChangePct' | 'timeframeChangePct'
  | 'score' | 'screenCount' | 'tech' | 'fund' | 'mom'
  | 'obOs' | 'upStreakDays' | 'trendLabel'
export type SortDir = 'asc' | 'desc'

export function filterRows(
  rows: C100Row[],
  opts: { search: string; sector: string; market: string }
): C100Row[] {
  const q = opts.search.trim().toLowerCase()
  return rows.filter(r => {
    if (q && !r.ticker.toLowerCase().includes(q) && !r.name.toLowerCase().includes(q)) return false
    if (opts.sector !== 'All' && r.sector !== opts.sector) return false
    if (opts.market !== 'All' && r.market !== opts.market) return false
    return true
  })
}

// How "hot" a horizon reads, so OB/OS sorts from most oversold to most
// overbought rather than alphabetically.
const OB_OS_WEIGHT: Record<ObOsState, number> = { OS: -1, N: 0, OB: 1 }

// The value a column actually sorts on. The one object-valued column (OB/OS)
// collapses to a number here so sortRows stays a single comparison path.
export function sortValue(row: C100Row, key: SortKey): string | number | null {
  if (key === 'obOs') {
    if (!row.obOs) return null
    return OB_OS_WEIGHT[row.obOs.st] * 4 + OB_OS_WEIGHT[row.obOs.it] * 2 + OB_OS_WEIGHT[row.obOs.lt]
  }
  const v = row[key]
  return v === undefined ? null : (v as string | number | null)
}

// Sorting never renumbers rank — `rank` travels with its row, so a name that
// published at #1 still reads #1 no matter which column the user sorts by.
export function sortRows(rows: C100Row[], key: SortKey, dir: SortDir): C100Row[] {
  const mult = dir === 'asc' ? 1 : -1
  const copy = [...rows]
  copy.sort((a, b) => {
    const av = sortValue(a, key)
    const bv = sortValue(b, key)
    // nulls always last, regardless of direction
    if (av === null && bv === null) return 0
    if (av === null) return 1
    if (bv === null) return -1
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mult
    return String(av).toLowerCase().localeCompare(String(bv).toLowerCase()) * mult
  })
  return copy
}

export function uniqueSectors(rows: C100Row[]): string[] {
  return Array.from(new Set(rows.map(r => r.sector).filter(Boolean))).sort()
}

export function uniqueMarkets(rows: C100Row[]): string[] {
  return Array.from(new Set(rows.map(r => r.market).filter(Boolean))).sort()
}

// "$10.4B" from 10_400_000_000. Kept here rather than in a cell so the value
// stays testable and the table has one formatting rule for size.
export function fmtMarketCap(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—'
  const abs = Math.abs(n)
  const [div, suffix] =
    abs >= 1e12 ? [1e12, 'T'] : abs >= 1e9 ? [1e9, 'B'] : abs >= 1e6 ? [1e6, 'M'] : [1e3, 'K']
  const scaled = n / div
  return `$${scaled >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)}${suffix}`
}
